import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

type AuthLayoutProps = {
  heading: string;
  leftContent: ReactNode;
  rightContent: ReactNode;
};

export default function AuthLayout({
  heading,
  leftContent,
  rightContent,
}: AuthLayoutProps) {
  const navigate = useNavigate();

  return (
    <div className="auth-page font-mono">
      <style>{`
        .auth-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .auth-page { min-height: 100vh; display: flex; flex-direction: column; }

        /* ===== NAV ===== */
        .auth-nav { display: flex; align-items: center; justify-content: space-between; padding: 16px 32px; flex-shrink: 0; box-shadow: 0 1px 0 rgba(0,0,0,0.08); }
        .auth-nav-logo { display: flex; align-items: center; gap: 8px; text-decoration: none; }
        .auth-nav-logo-mark { width: 14px; height: 14px; border-radius: 2px; flex-shrink: 0; }
        .auth-nav-link { cursor: pointer; transition: color 150ms; background: none; border: none; padding: 0; font-family: inherit; text-decoration: none; }
        .auth-nav-link:hover { color: #FFF; }

        /* ===== BODY ===== */
        .auth-body { flex: 1; display: flex; }

        /* ===== LEFT ===== */
        .auth-left { flex: 1; display: flex; align-items: center; justify-content: center; padding: 48px 40px; }
        .auth-left-inner { width: 100%; max-width: 420px; display: flex; flex-direction: column; gap: 24px; }

        /* ===== RIGHT ===== */
        .auth-right { width: 520px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; padding: 48px 56px 48px 72px; }
        .auth-right-inner { width: 100%; max-width: 400px; display: flex; flex-direction: column; gap: 16px; }
        .auth-unauthenticated-note { text-align: center; line-height: 1.55; }

        /* ===== SHADCN OVERRIDES INSIDE .auth-form ===== */
        .auth-form input[type="text"],
        .auth-form input[type="email"],
        .auth-form input[type="password"] {
          font-family: 'Roboto Mono', monospace;
          font-size: 13px;
          height: 48px;
          border: 1px solid var(--ds-border-200);
          border-radius: 8px;
          padding: 0 16px;
          background: #FFF;
          color: var(--ds-navy-900);
          transition: border-color 150ms;
          width: 100%;
        }
        .auth-form input:focus {
          border-color: var(--ds-blue-600);
          outline: none;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .auth-form button[type="submit"] {
          font-family: 'Roboto Mono', monospace;
          font-size: 14px;
          height: 48px;
          background: var(--ds-green-600);
          color: #fff;
          border: none;
          border-radius: 999px;
          font-weight: 700;
          cursor: pointer;
          transition: all 150ms;
          width: 100%;
        }
        .auth-form button[type="submit"]:hover {
          background: var(--ds-green-500);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(34,197,94,0.3);
        }
        .auth-form .auth-oauth-btn {
          font-family: 'Roboto Mono', monospace;
          font-size: 12px;
          height: 44px;
          background: transparent;
          color: var(--ds-navy-900);
          border: 1px solid var(--ds-border-200);
          border-radius: 999px;
          cursor: pointer;
          transition: all 150ms;
        }
        .auth-form .auth-oauth-btn:hover {
          border-color: var(--ds-blue-600);
          background: rgba(37,99,235,0.04);
        }
        .auth-form label {
          font-weight: 500;
          color: #374151;
        }
        .auth-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          color: var(--ds-text-500);
          font-size: 12px;
        }
        .auth-divider::before,
        .auth-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--ds-border-200);
        }

        /* ===== MOBILE ===== */
        @media (max-width: 768px) {
          .auth-nav { padding: 14px 20px; }
          .auth-body { flex-direction: column; }
          .auth-left { padding: 32px 20px; }
          .auth-right { width: 100%; padding: 32px 20px; }
        }
      `}</style>

      <nav className="auth-nav bg-navy-900">
        <Link to="/" className="auth-nav-logo">
          <span className="auth-nav-logo-mark bg-ds-green-500" />
          <span className="font-heading text-lg font-bold text-white">
            NIS2PASS
          </span>
        </Link>
        <div className="flex items-center gap-6">
          <button
            type="button"
            className="auth-nav-link text-xs text-ds-text-400"
            onClick={() => navigate("/termeni-si-conditii")}
          >
            Legal
          </button>
          <button
            type="button"
            className="auth-nav-link text-xs text-ds-text-400"
            onClick={() => navigate("/contact")}
          >
            Contact
          </button>
        </div>
      </nav>

      <div className="auth-body">
        <div className="auth-left bg-ds-surface-100">
          <div className="auth-left-inner">
            <h1 className="font-heading text-[42px] font-bold text-navy-900 leading-[1.1]">
              {heading}
            </h1>
            <div className="auth-form">{leftContent}</div>
          </div>
        </div>

        <div className="auth-right bg-navy-900">
          <div className="auth-right-inner">{rightContent}</div>
        </div>
      </div>
    </div>
  );
}
